import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ApiCustomerBank, customerBankCode, customerBankLabel, mapCustomerBanks } from '../../../../core/models/bank-details.models';
import { UserBankAccount, UserProfile } from '../../../../core/models/auth.models';
import { AuthService } from '../../../../core/services/auth.service';
import { BankAccountService } from '../../../../core/services/bank-account.service';
import { SouscriptionApiService } from '../../../../core/services/souscription-api.service';
import { UserService } from '../../../../core/services/user.service';
import {
  compressImageToBase64,
  formatBirthDateForApi,
  formatBirthDateForDisplay,
} from '../../../../core/utils/registration.utils';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy {
  @ViewChild('birthDateNative') birthDateNative?: ElementRef<HTMLInputElement>;
  @ViewChild('photoInput') photoInput?: ElementRef<HTMLInputElement>;

  profile: UserProfile | null = null;
  loading = true;
  savingProfile = false;
  savingBank = false;
  photoBusy = false;

  profileError = '';
  profileSuccess = '';
  bankError = '';
  bankSuccess = '';

  banks: ApiCustomerBank[] = [];
  banksLoading = false;
  banksError = false;

  photoPreview: string | null = null;
  photoBase64 = '';
  private photoDirty = false;
  readonly maxBirthDate = this.todayIso();
  readonly minBirthDate = '1900-01-01';

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    address: ['', [Validators.maxLength(200)]],
    birthDate: ['', [Validators.required, this.birthDateValidator.bind(this)]],
    niu: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9]{14}$/)]],
  });

  readonly bankForm = this.fb.group({
    bankId: ['', [Validators.required]],
    accountNumber: ['', [Validators.required]],
  });

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly bankAccountService: BankAccountService,
    private readonly souscriptionApi: SouscriptionApiService,
  ) {}

  ngOnInit(): void {
    this.authService.userProfile$.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
      this.profile = profile;
      this.loading = false;
      if (!profile) {
        return;
      }

      if (!this.savingProfile && !this.form.dirty && !this.photoDirty) {
        this.patchFormFromProfile(profile);
        return;
      }

      this.ensureCurrentBankOption();
      this.patchBankForm(profile);
    });

    this.loadBanks();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get firstNameDisplay(): string {
    const firstName = this.cleanValue(this.profile?.fName);
    return firstName || this.authService.getDisplayName(this.profile);
  }

  get phoneDisplay(): string {
    return this.formatPhone(this.profile);
  }

  get avatarInitial(): string {
    return (this.firstNameDisplay.charAt(0) || 'U').toUpperCase();
  }

  fieldInvalid(name: 'name' | 'firstName' | 'address' | 'birthDate' | 'niu'): boolean {
    const control = this.form.controls[name];
    return control.touched && control.invalid;
  }

  fieldError(name: 'name' | 'firstName' | 'birthDate' | 'niu'): string | null {
    const control = this.form.controls[name];
    if (!control.touched || control.valid) {
      return null;
    }

    if (name === 'name') {
      if (control.hasError('required')) {
        return 'Le nom est obligatoire';
      }
      if (control.hasError('minlength')) {
        return 'Le nom doit contenir au moins 2 caractères';
      }
    }

    if (name === 'firstName') {
      if (control.hasError('required')) {
        return 'Le prénom est obligatoire';
      }
      if (control.hasError('minlength')) {
        return 'Le prénom doit contenir au moins 2 caractères';
      }
    }

    if (name === 'birthDate') {
      if (control.hasError('required')) {
        return 'La date de naissance est obligatoire';
      }
      if (control.hasError('birthDateFormat')) {
        return 'Format invalide. Utilisez le format JJ/MM/AAAA';
      }
      if (control.hasError('birthDateMinor')) {
        return 'Vous devez avoir au moins 18 ans';
      }
      if (control.hasError('birthDateFuture')) {
        return 'La date de naissance ne peut pas être dans le futur';
      }
      return 'Date invalide';
    }

    if (name === 'niu') {
      if (control.hasError('required')) {
        return "Le Numéro unique d'identification (NIU) est obligatoire";
      }
      return 'Le NIU doit contenir 14 caractères';
    }

    return null;
  }

  bankFieldInvalid(name: 'bankId' | 'accountNumber'): boolean {
    const control = this.bankForm.controls[name];
    return control.touched && control.invalid;
  }

  openPhotoPicker(): void {
    this.photoInput?.nativeElement.click();
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.profileError = 'Veuillez sélectionner une image.';
      this.profileSuccess = '';
      return;
    }

    this.photoBusy = true;
    this.profileError = '';
    compressImageToBase64(file)
      .then((base64) => {
        this.photoBase64 = base64;
        this.photoPreview = `data:image/jpeg;base64,${base64}`;
        this.photoDirty = true;
      })
      .catch(() => {
        this.profileError = 'Impossible de lire cette image.';
      })
      .finally(() => {
        this.photoBusy = false;
      });
  }

  openDatePicker(): void {
    const native = this.birthDateNative?.nativeElement as
      | (HTMLInputElement & { showPicker?: () => void })
      | undefined;
    if (!native) {
      return;
    }

    const iso = formatBirthDateForApi(this.form.controls.birthDate.value ?? '');
    native.value = iso ?? native.value;
    if (typeof native.showPicker === 'function') {
      native.showPicker();
      return;
    }
    native.click();
  }

  onNativeDateChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value) {
      return;
    }

    const display = formatBirthDateForDisplay(value);
    this.form.controls.birthDate.setValue(display);
    this.form.controls.birthDate.markAsTouched();
  }

  saveProfile(): void {
    this.profileError = '';
    this.profileSuccess = '';
    this.form.markAllAsTouched();
    if (this.form.invalid || this.savingProfile) {
      return;
    }

    const username = this.authService.getUsername() || this.profile?.phone || '';
    if (!username) {
      this.profileError = 'Profil indisponible pour le moment.';
      return;
    }

    const raw = this.form.getRawValue();
    const name = (raw.name ?? '').trim();
    const firstName = (raw.firstName ?? '').trim();
    const address = (raw.address ?? '').trim();
    const dateNaissance = (raw.birthDate ?? '').trim();
    const niu = (raw.niu ?? '').trim().toUpperCase();
    const profileImage = this.photoBase64;

    this.savingProfile = true;
    this.userService
      .updateProfile({
        username,
        name,
        firstName,
        dateNaissance,
        niu,
        address,
        profileImage,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.savingProfile = false;
          if (response?.code === 200 || response?.status === 'SUCCESS') {
            const current = this.profile;
            this.authService.patchProfile({
              name,
              fName: firstName,
              lName: !current?.lName || current.lName === current.name ? name : current.lName,
              address,
              niu,
              dateNaiss: dateNaissance,
              photoProfil: profileImage || current?.photoProfil,
            });
            this.form.markAsPristine();
            this.photoDirty = false;
            this.profileSuccess = 'Profil mis à jour avec succès';
            return;
          }

          this.profileError = response?.message || 'Échec de la mise à jour du profil';
        },
        error: () => {
          this.savingProfile = false;
          this.profileError = 'Échec de la mise à jour du profil';
        },
      });
  }

  saveBank(): void {
    this.bankError = '';
    this.bankSuccess = '';
    this.bankForm.markAllAsTouched();
    if (this.bankForm.invalid || this.savingBank) {
      this.bankError = this.bankForm.invalid
        ? 'Veuillez remplir tous les champs obligatoires'
        : '';
      return;
    }

    const userId = this.authService.getUserId();
    const username = this.authService.getUsername() || this.profile?.phone || '';
    if (!userId || !username) {
      this.bankError = 'Profil indisponible pour le moment.';
      return;
    }

    const bankId = this.bankForm.controls.bankId.value?.trim() ?? '';
    const bank = this.banks.find((item) => customerBankCode(item) === bankId);
    const bankName = bank ? customerBankLabel(bank) : bankId;
    const accountNumber = this.bankForm.controls.accountNumber.value?.trim() ?? '';

    this.savingBank = true;
    this.bankAccountService
      .updateBankAccount({
        bankName,
        bankCode: bankId,
        accountNumber,
        userId,
        username,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingBank = false;
          this.bankSuccess = 'Informations bancaires mises à jour avec succès';
          const nextAccount: UserBankAccount = {
            ...(this.profile?.bankAccounts?.[0] ?? {}),
            bankName,
            bankCode: bankId,
            accountNumber,
          };
          this.authService.patchProfile({ bankAccounts: [nextAccount] });
        },
        error: (err: unknown) => {
          this.savingBank = false;
          const http = err as { error?: { message?: string }; message?: string };
          const message = String(http?.error?.message || http?.message || '')
            .replace('Exception:', '')
            .trim();
          this.bankError = message
            ? `Erreur: ${message}`
            : 'Impossible de mettre à jour les informations bancaires.';
        },
      });
  }

  loadBanks(): void {
    this.banksLoading = true;
    this.banksError = false;
    this.souscriptionApi
      .fetchInitData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          const record = data as Record<string, unknown> | null;
          this.banks = mapCustomerBanks(
            record?.['banque_client'] ?? record?.['banks'] ?? data?.banque_client ?? data?.banks,
          );
          this.ensureCurrentBankOption();
          this.patchBankForm(this.profile);
          this.banksLoading = false;
        },
        error: () => {
          this.banksLoading = false;
          this.banksError = true;
          this.ensureCurrentBankOption();
          this.patchBankForm(this.profile);
        },
      });
  }

  bankCode(bank: ApiCustomerBank): string {
    return customerBankCode(bank);
  }

  bankLabel(bank: ApiCustomerBank): string {
    return customerBankLabel(bank);
  }

  private patchFormFromProfile(profile: UserProfile): void {
    this.form.reset({
      name: this.cleanValue(profile.name),
      firstName: this.cleanValue(profile.fName),
      address: this.cleanValue(profile.address),
      birthDate: formatBirthDateForDisplay(profile.dateNaiss),
      niu: this.cleanValue(profile.niu),
    });

    this.photoBase64 = this.stripDataUri(profile.photoProfil ?? '');
    this.photoPreview = this.toPhotoSrc(profile.photoProfil);
    this.patchBankForm(profile);
  }

  private patchBankForm(profile: UserProfile | null): void {
    const account = profile?.bankAccounts?.[0];
    if (!account) {
      return;
    }

    const bankId = (account.bankCode ?? '').trim();
    if (bankId) {
      this.bankForm.controls.bankId.setValue(bankId);
    }
    if (account.accountNumber) {
      this.bankForm.controls.accountNumber.setValue(account.accountNumber);
    }
  }

  private ensureCurrentBankOption(): void {
    const account = this.profile?.bankAccounts?.[0];
    const code = (account?.bankCode ?? '').trim();
    if (!code || this.banks.some((bank) => customerBankCode(bank) === code)) {
      return;
    }

    this.banks = [
      { CODE: code, LABEL: account?.bankName || code },
      ...this.banks,
    ];
  }

  private formatPhone(profile: UserProfile | null): string {
    const phone = (profile?.phone ?? '').trim();
    if (!phone) {
      return '—';
    }

    const dial = (profile?.codePays ?? '').trim();
    if (!dial) {
      return phone;
    }

    const normalizedDial = dial.startsWith('+') ? dial : `+${dial}`;
    const dialDigits = normalizedDial.replace('+', '');
    const phoneDigits = phone.replace(/[\s\-+]/g, '');

    if (phoneDigits.startsWith(dialDigits)) {
      const localNumber = phoneDigits.slice(dialDigits.length);
      return localNumber ? `${normalizedDial} ${localNumber}` : normalizedDial;
    }

    return `${normalizedDial} ${phone}`;
  }

  private toPhotoSrc(raw: string | null | undefined): string | null {
    const value = (raw ?? '').trim();
    if (!value || value === '-') {
      return null;
    }
    if (value.startsWith('data:') || value.startsWith('http')) {
      return value;
    }
    return `data:image/jpeg;base64,${value}`;
  }

  private stripDataUri(raw: string): string {
    const value = raw.trim();
    if (!value || value === '-') {
      return '';
    }
    return value.includes(',') ? value.split(',')[1] : value;
  }

  private cleanValue(raw: string | null | undefined): string {
    const value = (raw ?? '').trim();
    return !value || value === '-' ? '' : value;
  }

  private todayIso(): string {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${today.getFullYear()}-${month}-${day}`;
  }

  private birthDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value as string)?.trim();
    if (!value) {
      return null;
    }

    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
      return { birthDateFormat: true };
    }

    const [, day, month, year] = match.map(Number);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return { birthDateInvalid: true };
    }

    const today = new Date();
    if (date > today) {
      return { birthDateFuture: true };
    }

    const age =
      today.getFullYear() -
      year -
      (today.getMonth() < month - 1 || (today.getMonth() === month - 1 && today.getDate() < day) ? 1 : 0);

    if (age < 18) {
      return { birthDateMinor: true };
    }
    if (age > 120) {
      return { birthDateTooOld: true };
    }

    return null;
  }
}
